import * as PIXI from "pixi.js";
import { Component } from "kernelplay-js";

export class PixiShapeRenderer extends Component {
  constructor(props = {}) {
    super();

    const {
      shape       = "rect",     // "rect" | "circle" | "ellipse" | "triangle" | "polygon" | "line"
      width       = 50,
      height      = 50,
      radius      = 25,         // for circle
      radiusX     = 40,         // for ellipse
      radiusY     = 20,         // for ellipse
      points      = [],         // for polygon [ {x,y}, ... ] or line [ {x,y}, {x,y} ]
      color       = 0xff0000,
      alpha       = 1.0,
      zIndex      = 0,
      filled      = true,
      strokeColor = 0xffffff,
      strokeWidth = 0,          // 0 = no stroke
      anchor      = { x: 0.5, y: 0.5 },
    } = props;

    this.shape       = shape;
    this.width       = width;
    this.height      = height;
    this.radius      = radius;
    this.radiusX     = radiusX;
    this.radiusY     = radiusY;
    this.points      = points;
    this.color       = color;
    this.alpha       = alpha;
    this.zIndex      = zIndex;
    this.filled      = filled;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.anchor      = anchor;

    // Internal
    this.object  = null;   // PIXI.Graphics — picked up by WebGL2DRenderer
    this._dirty  = true;
    this.flipX   = false;
    this.flipY   = false;
  }

  init() {
    this.transform = this.entity.getComponent("transform");
    // this._build();
  }

  onStart(){
    this._build();
  }

  // ── Build PIXI.Graphics ───────────────────────────────────────────────

  _build() {
    // Clean up old object if rebuilt
    if (this.object) {
      this.object.parent?.removeChild(this.object);
      this.object.destroy();
      this.object = null;
    }

    const gfx = new PIXI.Graphics();
    this._draw(gfx);

    gfx.alpha   = this.alpha;
    gfx.zIndex  = this.zIndex;

    this.object = gfx;

    const stage = this.entity.scene?.game?.renderer?.app?.stage;
    if (stage) {
      stage.addChild(this.object);
      stage.sortableChildren = true;
    }

    this._dirty = false;
  }

  _draw(gfx) {
    gfx.clear();

    // Stroke
    if (this.strokeWidth > 0) {
      gfx.setStrokeStyle({ width: this.strokeWidth, color: this.strokeColor });
    }

    // Anchor offset
    const ox = -this.width  * this.anchor.x;
    const oy = -this.height * this.anchor.y;

    switch (this.shape) {

      case "rect":
        if (this.filled) gfx.rect(ox, oy, this.width, this.height).fill(this.color);
        if (this.strokeWidth > 0) gfx.rect(ox, oy, this.width, this.height).stroke();
        break;

      case "roundedrect":
        if (this.filled) gfx.roundRect(ox, oy, this.width, this.height, this.radius).fill(this.color);
        if (this.strokeWidth > 0) gfx.roundRect(ox, oy, this.width, this.height, this.radius).stroke();
        break;

      case "circle":
        if (this.filled) gfx.circle(0, 0, this.radius).fill(this.color);
        if (this.strokeWidth > 0) gfx.circle(0, 0, this.radius).stroke();
        break;

      case "ellipse":
        if (this.filled) gfx.ellipse(0, 0, this.radiusX, this.radiusY).fill(this.color);
        if (this.strokeWidth > 0) gfx.ellipse(0, 0, this.radiusX, this.radiusY).stroke();
        break;

      case "triangle":
        const tx = this.width  / 2;
        const ty = this.height / 2;
        const triPoints = [
          { x:  0,  y: -ty },
          { x:  tx, y:  ty },
          { x: -tx, y:  ty },
        ];
        this._drawPolygon(gfx, triPoints);
        break;

      case "polygon":
        this._drawPolygon(gfx, this.points);
        break;

      case "line":
        if (this.points.length < 2) break;
        gfx.setStrokeStyle({ width: this.strokeWidth || 2, color: this.color });
        gfx.moveTo(this.points[0].x, this.points[0].y);
        for (let i = 1; i < this.points.length; i++) {
          gfx.lineTo(this.points[i].x, this.points[i].y);
        }
        gfx.stroke();
        break;

      default:
        console.warn(`ShapeRenderer: unknown shape "${this.shape}"`);
    }
  }

  _drawPolygon(gfx, pts) {
    if (pts.length < 3) return;
    const flat = pts.flatMap(p => [p.x, p.y]);
    if (this.filled)            gfx.poly(flat).fill(this.color);
    if (this.strokeWidth > 0)   gfx.poly(flat).stroke();
  }

  // ── Public API ────────────────────────────────────────────────────────

  /** Change any property and redraw */
  setShape(shape) {
    this.shape = shape;
    this._build();
  }

  setColor(color) {
    this.color = color;
    if (this.object) this._draw(this.object);
  }

  setSize(width, height) {
    this.width  = width;
    this.height = height ?? width;
    if (this.object) this._draw(this.object);
  }

  setRadius(radius) {
    this.radius = radius;
    if (this.object) this._draw(this.object);
  }

  setAlpha(alpha) {
    this.alpha = alpha;
    if (this.object) this.object.alpha = alpha;
  }

  setStroke(color, width) {
    this.strokeColor = color;
    this.strokeWidth = width;
    if (this.object) this._draw(this.object);
  }

  setFilled(filled) {
    this.filled = filled;
    if (this.object) this._draw(this.object);
  }

  setPoints(points) {
    this.points = points;
    if (this.object) this._draw(this.object);
  }

  // ── Bounds — used by frustum culling ─────────────────────────────────

  getBounds() {
    const t = this.transform;
    if (!t) return null;

    const w = this.width  * t.scale.x;
    const h = this.height * t.scale.y;

    return {
      x:      t.position.x - w * this.anchor.x,
      y:      t.position.y - h * this.anchor.y,
      width:  w,
      height: h,
    };
  }

  // ── Cleanup ───────────────────────────────────────────────────────────

  onDestroy() {
    if (this.object) {
      this.object.parent?.removeChild(this.object);
      this.object.destroy();
      this.object = null;
    }
  }

  // ── Serialization ─────────────────────────────────────────────────────

  toJSON() {
    return {
      type:        "ShapeRenderer",
      shape:       this.shape,
      width:       this.width,
      height:      this.height,
      radius:      this.radius,
      radiusX:     this.radiusX,
      radiusY:     this.radiusY,
      points:      this.points,
      color:       this.color,
      alpha:       this.alpha,
      zIndex:      this.zIndex,
      filled:      this.filled,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      anchor:      this.anchor,
    };
  }

  static fromJSON(data) {
    return new ShapeRenderer(data);
  }
}